import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { UserBusinessUnitAuthGuard } from '../auth/auth-guard/user-business-unit.authguard';
import { ReportsBusinessUnitNavGuard } from '@max/auth/auth-guard/reports-business-unit.navGuard';
import { UserLoggedInAuthGuard } from '../auth/auth-guard/user-logged-in.authGuard';
import { UserTransactionAuthGuard } from '../auth/auth-guard/user-transaction.authguard';
import { UserTransactionNavGuard } from '../auth/auth-guard/user-transaction.navGuard';
import { ClaimsService } from '../auth/services/claims.service';
import { CorrespondenceInstanceService } from '../correspondence/services/correspondence-instance.service';
import { CorrespondenceTemplateService } from '../correspondence/services/correspondence-template.service';
import { CustomerOmnisearchResultsService } from '../customer/search/customer-omnisearch-results.service';
import { CartService } from '../finance/shared/services/cart.service';
import { AAMVAIntegrationService } from '../titleregistration/services/aamvaIntegration.service';
import { TransactionService } from '../titleregistration/services/transaction.service';
import { VehicleMediatorService } from '../titleregistration/services/vehicle-mediator.service';
import { WipDocumentService } from '../titleregistration/services/wip-document.service';
import { CheckinService } from '../transaction/services/checkin.service';
import { WipSearchService } from '../transaction/services/wip-search.service';
import { AutoCompleteService } from './components/autocomplete/autocomplete.service';
import { TitleRegistrationTransactionSummaryService } from './components/template/title-registration-transaction-summary.service';
import { TransactionSummaryFactory } from './components/template/transaction-summary-factory.service';
import { BusinessTransactionService } from './services/business-transaction/business-transaction.service';
import { RedirectTransactionService } from './services/business-transaction/redirect-transaction.service';
import { DocumentService } from './services/document/document.service';
import { FormValidationService } from './services/forms/form-validation.service';
import { HelpPanelCommunicationService } from './services/help-panel/help-panel-communication.service';
import { MenuItemsService } from './services/menu-items/menu-items.service';
import { NavigationService } from './services/navigation/navigation.service';
import { OmegaService } from './services/omega/omega.service';
import { DocumentViewerService } from './services/output/document-viewer.service';
import { PrintService } from './services/output/print.service';
import { ChannelService } from './services/realtime-messaging/channel.service';
import { SystemNotificationService } from './services/realtime-messaging/system-notification.service';
import { CaseRouteBuilderService } from './services/route-builder/case-route-builder.service';
import { DriverRouteBuilderService } from './services/route-builder/driver-route-builder.service';
import { FinanceRouteBuilderService } from './services/route-builder/finance-route-builder.service';
import { CollectionsRouteBuilderService } from './services/route-builder/collections-route-builder.service';
import { HelpDeskRouteBuilderService } from './services/route-builder/helpdesk-route-builder.service';
import { ProhibitionComplianceRouteBuilderService } from './services/route-builder/prohibition-compliance-route-builder.service';
import { RouteBuilderFactory } from './services/route-builder/route-builder-factory.service';
import { TitleRegistrationRouteBuilderService } from './services/route-builder/title-registration-route-builder.service';
import { RouteConfigurationFactory } from './services/route-configuration/route-configuration-factory.service';
import { TemplateConfigurationFactory } from './services/route-configuration/template-configuration-factory.service';
import { TransactionRouteConfigurationService } from './services/route-configuration/transaction-route-configuration.service';
import { TransactionTemplateConfigurationService } from './services/route-configuration/transaction-template-configuration.service';
import { RouterOutletService } from './services/router-outlet/router-outlet.service';
import { RouterService } from './services/router/router.service';
import { CaseTransactionOverrideService } from './services/transaction-override/case-transaction-override.service';
import { DriverTransactionOverrideService } from './services/transaction-override/driver-transaction-override.service';
import { FinanceTransactionOverrideService } from './services/transaction-override/finance-transaction-override.service';
import { ProhibitionComplianceTransactionOverrideService } from './services/transaction-override/prohibition-compliance-transaction-override.service';
import { TitleRegistrationTransactionOverrideService } from './services/transaction-override/title-registration-transaction-override.service';
import { TransactionOverrideFactory } from './services/transaction-override/transaction-override-factory.service';
import { ValidationResultsService } from './services/validation-results/validation-results.service';
import { ValidationService } from './services/validation-results/validation.service';
import { WorkflowService } from './services/workflow/workflow.service';
import { ControlValidationMessagesService } from './validators/control-validation-messages.service';
import { AssistedSelfServiceInvitationService } from './services/assisted-self-service-invitation/assisted-self-service-invitation.service';
import { OmniSearchAuthGuard } from '../auth/auth-guard/omni-search.authGuard';
import { IRPRouteBuilderService } from './services/route-builder/irp-route-builder.service';
import { CommercialPermittingRouteBuilderService } from './services/route-builder/commercial-permitting-route-builder.service';
import { IRPTransactionOverrideService } from './services/transaction-override/irp-transaction-override.service';
import { CommercialPermittingTransactionOverrideService } from './services/transaction-override/commercial-permitting-transaction-override.service';
import { AircraftRouteBuilderService } from './services/route-builder/aircraft-route-builder.service';
import { AircraftTransactionOverrideService } from './services/transaction-override/aircraft-transaction-override.service';
import { RedactionService } from './services/redaction/redaction.service';
import { TabToTabService } from './services/tab-to-tab-messaging/tab-to-tab.service';
import { PowerBIService } from './services/reports/power-bi.service';

@NgModule()
export class CoreModule {

    constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
        if (parentModule) {
            throw new Error(
                'CoreModule is already loaded. Import it in the AppModule only');
        }
    }

    static forRoot(): ModuleWithProviders<CoreModule> {
        return {
            ngModule: CoreModule,
            providers: [
                CorrespondenceInstanceService,
                CorrespondenceTemplateService,
                DocumentViewerService,
                PrintService,
                AutoCompleteService,
                ControlValidationMessagesService,
                BusinessTransactionService,
                WorkflowService,
                HelpPanelCommunicationService,
                ChannelService,
                SystemNotificationService,
                NavigationService,
                FormValidationService,
                CartService,
                FormGroupDirective,
                RedirectTransactionService,
                WipDocumentService,
                UserTransactionNavGuard,
                UserTransactionAuthGuard,
                UserLoggedInAuthGuard,
                UserBusinessUnitAuthGuard,
                ReportsBusinessUnitNavGuard,
                OmniSearchAuthGuard,
                ClaimsService,
                RouterService,
                RouterOutletService,
                ValidationResultsService,
                CaseRouteBuilderService,
                DriverRouteBuilderService,
                TitleRegistrationRouteBuilderService,
                FinanceRouteBuilderService,
                CollectionsRouteBuilderService,
                ProhibitionComplianceRouteBuilderService,
                HelpDeskRouteBuilderService,
                RouteBuilderFactory,
                TransactionService,
                AAMVAIntegrationService,
                VehicleMediatorService,
                TransactionRouteConfigurationService,
                TransactionTemplateConfigurationService,
                RouteConfigurationFactory,
                TemplateConfigurationFactory,
                TransactionSummaryFactory,
                TitleRegistrationTransactionSummaryService,
                DocumentService,
                ValidationService,
                WipSearchService, // temporary
                CheckinService, // temporary
                MenuItemsService,
                CustomerOmnisearchResultsService,
                TransactionOverrideFactory,
                TitleRegistrationTransactionOverrideService,
                FinanceTransactionOverrideService,
                DriverTransactionOverrideService,
                ProhibitionComplianceTransactionOverrideService,
                CaseTransactionOverrideService,
                OmegaService,
                AssistedSelfServiceInvitationService,
                IRPRouteBuilderService,
                IRPTransactionOverrideService,
                CommercialPermittingRouteBuilderService,
                AircraftRouteBuilderService,
                CommercialPermittingTransactionOverrideService,
                AircraftTransactionOverrideService,
                RedactionService,
                TabToTabService,
                PowerBIService
            ]
        };
    }
}
